import { Component, Input, Output, OnInit, AfterViewInit, EventEmitter } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import {
   debounceTime, takeWhile,
   filter, startWith } from 'rxjs/operators';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DimsUom, setCalcGroupValidators } from '@library/common/models';
import { ValidateFormFields } from '@library/common/functions';
import { WeightConvert } from '@library/core/pipes';

import { ShipmentService } from '../shipment.service';

@UntilDestroy()
@Component({
   selector: 'calculator-item',
   templateUrl: 'calculator-item.component.html',
   styleUrls: [ './calculator-item.component.scss' ]
})
export class CalculatorItemComponent implements OnInit, AfterViewInit {

   @Input() index = 0;
   @Input() last = false;
   @Input()
   set calcForm( form: FormGroup ) {
      if ( form.get('__patch').disabled ) {
         this.weightGroup.disable();
   
         form.get('description').enable();
         form.get('reference').enable();
      }

      this._calcForm = form;
   }

   get calcForm() {
      return this._calcForm;
   }
   
   private _calcForm: FormGroup;

   @Output() remove = new EventEmitter<any>();
   @Output() dirty = new EventEmitter<any>();

   _helper = false;
   get helper() { return this._helper; }
   set helper( value: boolean ) {
      this._helper = value;
      if ( !value ) {
         this.weightGroup.get( 'quantity' ).reset();
         this.weightGroup.get( 'weight' ).reset();
      }
   }

   weightGroup: FormGroup = new FormGroup({
      quantity: new FormControl( null, [ Validators.min( 1 ) ]),
      weight: new FormControl( null, [ Validators.min( 0 ) ]),
      weight_lbs: new FormControl(),
      weight_kgs: new FormControl(),
      weight_type: new FormControl( 'lbs' ), // lbs or kgs
   });

   measureTypes = DimsUom;

   get pieces() { return this._calcForm.get( 'pieces' ); }
   get weight() { return this._calcForm.get( 'weight' ); }
   get weight_type() { return this._calcForm.get( 'weight_type' ); }

   constructor( public shipment: ShipmentService, ) {}

   ngOnInit() {
      // emit on dirty if it's the last one in the array
      this._calcForm.valueChanges.pipe(
         takeWhile( _ => this.last ),
         filter( _ => this._calcForm.dirty ),
         debounceTime( 800 ),
         untilDestroyed( this ),
      ).subscribe( _ => {
         this._calcForm = setCalcGroupValidators( this._calcForm );

         if ( !this._calcForm.get('__patch').disabled ) {
            this.dirty.emit();
         }
      });

      // load weights into weightGroup
      if ( this.weight_type.value ) {
         this.weightGroup.get( 'weight_type' ).setValue( this.weight_type.value );
      }
      if ( this.weight.value ) {
         this.weightGroup.get( `weight_${this.weight_type.value}` ).setValue( this.weight.value );
      }

      // set metric on ocean shipments
      // if ( this.shipment.shipType === 'international' ) {
      //    this.weightGroup.get( 'weight_type' ).setValue( 'kgs', { emitEvent: false });
      //    this.weight_type.setValue( 'kgs' );
      //    if ( ![ 'cm', 'm' ].includes( this.calcForm.get( 'measure_type' ).value )) {
      //       this.calcForm.get( 'measure_type' ).setValue( 'cm' );
      //    }
      // }

      // set applicable validators
      this.weightGroup.get( 'weight_type' ).valueChanges.pipe(
         startWith( this.weightGroup.get( 'weight_type' ).value ),
         untilDestroyed( this ),
      ).subscribe( type => {
         this.weight_type.setValue( type, { emitEvent: false });

         switch ( type ) {
            case 'lbs':
               this.weightGroup.get( 'weight_kgs' ).clearValidators();
               this.weightGroup.get( `weight_lbs` ).setValidators( this._calcForm.get( 'weight' ).validator );
               break;
            case 'kgs':
               this.weightGroup.get( 'weight_lbs' ).clearValidators();
               this.weightGroup.get( `weight_kgs` ).setValidators( this._calcForm.get( 'weight' ).validator );
               break;
         }

         this.weightGroup.get( 'weight_lbs' ).updateValueAndValidity({ onlySelf: true, emitEvent: false });
         this.weightGroup.get( `weight_kgs` ).updateValueAndValidity({ onlySelf: true, emitEvent: false });
      });

      // listen for weightGroup changes and update accordingly
      this.weightGroup.valueChanges.pipe(
         startWith( this.weightGroup.value ),
         untilDestroyed( this ),
      ).subscribe( change => {
         const inverse = { lbs: 'kgs', kgs: 'lbs' };

         if ( !this.helper ) {
            this.weightGroup.get( 'quantity' ).reset( null, { emitEvent: false });
            this.weightGroup.get( 'weight' ).reset( null, { emitEvent: false });
         } else if ( change.quantity && change.weight ) {
            const total_weight = +( change.quantity * change.weight ).toFixed( 2 );
            this.weightGroup.get( `weight_${change.weight_type}` ).setValue( total_weight, { emitEvent: false });
            change[`weight_${change.weight_type}`] = total_weight;
         }

         if ( !change[`weight_${change.weight_type}`] ) {
            return;
         }

         this.weightGroup.get( `weight_${inverse[change.weight_type]}` ).setValue(
            +WeightConvert( change[`weight_${change.weight_type}`], change.weight_type, inverse[ change.weight_type ])?.toFixed( 2 ),
            { emitEvent: false }
         )
         setTimeout( _ => {
            this._calcForm.markAsDirty();
            this._calcForm.get( 'weight' ).setValue( change[`weight_${change.weight_type}`] );
         });
      });

      // listen for highlight required call
      this.shipment.highlightInvalid$.pipe(
         filter( _ => _ ),
         untilDestroyed( this ),
      ).subscribe( _ => ValidateFormFields( this.weightGroup ));
   }

   ngAfterViewInit() {
      if ( this.index < 1 ) {
         setTimeout( _ => {
            this._calcForm.markAsDirty();

            if ( !this._calcForm.get( 'pieces').value ) {
               this._calcForm.get( 'pieces' ).setValue( 1 );
            }

            if ( !this._calcForm.get( 'weight').value ) {
               this._calcForm.get( 'weight' ).setValue( 1 );
               this.weightGroup.get( `weight_${this.weight_type.value}` ).setValue( this.weight.value );
            }

            setCalcGroupValidators( this._calcForm );
            this._calcForm.updateValueAndValidity();
         });
      }
   }
}
